/**
 * Permission predicate + the four-eyes pairing matrix.
 *
 * Two layers:
 *
 *   1. `hasPermission(role, action)` — yes/no per single operator. Drives
 *      `usePermission(action)` in components, route guards, and server
 *      action checks. Pure, synchronous, defended by the closed
 *      Permission vocabulary in ./roles.ts.
 *
 *   2. `canApproveAs(role, action, requesterRole)` — yes/no on whether a
 *      *second* operator with the given role can act as the four-eyes
 *      approver for an action initiated by `requesterRole`.
 *      • Default: anyone with the permission can approve, except the
 *        requester themselves (enforced by id in `four-eyes.ts`).
 *      • Mandate-override-class actions REQUIRE Compliance on BOTH
 *        sides: requester role AND approver role must be 'compliance'.
 *        This is the hard institutional control.
 */

import type { OperatorRole } from '@/types/domain';

import { ROLE_PERMISSIONS, type Permission } from './roles';

/* ============================================================================
 * Single-actor permission
 * ============================================================================ */

export const hasPermission = (role: OperatorRole, action: Permission): boolean => {
  const granted = ROLE_PERMISSIONS[role];
  return granted.includes(action);
};

/* ============================================================================
 * Four-eyes-eligible action classes
 *
 * An action becomes a four-eyes flow when it appears in this set. The bulk
 * assignment wizard, rollout promotion, mandate override, kill-switch, and
 * a few admin verbs are gated this way.
 * ============================================================================ */

export const FOUR_EYES_REQUIRED: ReadonlySet<Permission> = new Set<Permission>([
  // Control plane
  'assignments:approve',
  'assignments:terminate',
  'rollouts:promote',
  'rollouts:abort',
  // Eligibility overrides
  'eligibility:override_approve',
  // Mandate
  'mandate:amend',
  'mandate:override_approve',
  // Risk
  'risk:kill_switch',
  'risk:override_limit',
  // Templates with broad blast radius
  'templates:apply',
  // Admin
  'admin:remove_user',
  'admin:change_role',
]);

/**
 * Action classes that require Compliance on BOTH the requester and approver
 * side. Adding to this set tightens institutional control — never silently
 * widens it.
 */
export const COMPLIANCE_ONLY_FOUR_EYES: ReadonlySet<Permission> = new Set<Permission>([
  'mandate:override_approve',
  'eligibility:override_approve',
  // Mandate amendments are operationally drastic — surface to compliance both sides.
  'mandate:amend',
]);

/* ============================================================================
 * Pairing predicate
 * ============================================================================ */

export interface ApprovalContext {
  /** The action being four-eyed. */
  action: Permission;
  /** Role of the operator who initiated the action. */
  requesterRole: OperatorRole;
  /** Id of the requesting operator (so the approver can't self-approve). */
  requesterId: string;
  /** Id of the proposed approver. */
  approverId: string;
  /** Role of the proposed approver. */
  approverRole: OperatorRole;
}

export type ApprovalRefusalReason =
  | 'requester_lacks_permission'
  | 'approver_lacks_permission'
  | 'self_approval_forbidden'
  | 'compliance_required_on_both_sides'
  | 'not_a_four_eyes_action';

export const canApprove = (
  ctx: ApprovalContext,
): { ok: true } | { ok: false; reason: ApprovalRefusalReason } => {
  if (!FOUR_EYES_REQUIRED.has(ctx.action)) {
    return { ok: false, reason: 'not_a_four_eyes_action' };
  }
  if (ctx.requesterId === ctx.approverId) {
    return { ok: false, reason: 'self_approval_forbidden' };
  }
  if (!hasPermission(ctx.requesterRole, ctx.action)) {
    return { ok: false, reason: 'requester_lacks_permission' };
  }
  if (!hasPermission(ctx.approverRole, ctx.action)) {
    return { ok: false, reason: 'approver_lacks_permission' };
  }
  if (COMPLIANCE_ONLY_FOUR_EYES.has(ctx.action)) {
    if (ctx.requesterRole !== 'compliance' || ctx.approverRole !== 'compliance') {
      return { ok: false, reason: 'compliance_required_on_both_sides' };
    }
  }
  return { ok: true };
};

/** Human-readable reason — surfaced in the refusal dialog. */
export const explainRefusal = (reason: ApprovalRefusalReason): string => {
  switch (reason) {
    case 'requester_lacks_permission':
      return 'The requesting operator does not have permission for this action.';
    case 'approver_lacks_permission':
      return 'The selected approver does not have permission for this action.';
    case 'self_approval_forbidden':
      return 'The same operator cannot both request and approve a four-eyes action.';
    case 'compliance_required_on_both_sides':
      return 'This action requires the Compliance role on both the requester and the approver.';
    case 'not_a_four_eyes_action':
      return 'This action does not require four-eyes approval.';
  }
};
